import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  FolderKanban,
  Calendar,
  MapPin,
  Users,
  ClipboardList,
  LogOut,
  ShieldCheck,
  Webhook,
  Sparkles,
  Activity,
  Rocket,
  CheckSquare,
  Clock,
  Building2,
  Lightbulb,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useBrand, BrandLogo } from "@/lib/brand";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { ClientsNavGroup } from "@/components/ClientsNavGroup";

const navItems = [
  // Launchpad / My Tasks / Projects are visible to client-role users too —
  // the backend already scopes their reads to only the projects they are
  // members of, so we can safely expose the entry points in the sidebar.
  { to: "/launchpad", label: "Launchpad", icon: Rocket, end: false, testId: "nav-launchpad" },
  { to: "/my-tasks", label: "My Tasks", icon: CheckSquare, end: false, testId: "nav-my-tasks" },
  { to: "/", label: "Projects", icon: FolderKanban, end: true, testId: "nav-projects" },
  // internalOnly = hidden for client-side users (User.client_id populated).
  // Client users get redirected to their own /clients/{their_client_id}
  // page (Planning Calendar lives there as an embedded section).
  { to: "/clients", label: "Clients", icon: Building2, end: false, testId: "nav-clients", internalOnly: true },
  { to: "/studio", label: "Studio", icon: Activity, end: false, testId: "nav-studio", adminOnly: true, internalOnly: true },
  { to: "/call-sheets", label: "Call Sheets", icon: ClipboardList, end: false, testId: "nav-call-sheets", internalOnly: true },
  { to: "/locations", label: "Locations", icon: MapPin, end: false, testId: "nav-locations", adminOnly: true, internalOnly: true },
  { to: "/team", label: "Team", icon: Users, end: false, testId: "nav-team", adminOnly: true, internalOnly: true },
  { to: "/calendar", label: "Calendar", icon: Calendar, end: false, testId: "nav-calendar", internalOnly: true },
];

const adminNavItems = [
  { to: "/admin/users", label: "Users", icon: ShieldCheck, testId: "nav-users" },
  { to: "/admin/webhooks", label: "Webhooks", icon: Webhook, testId: "nav-webhooks" },
  { to: "/admin/weekly-updates", label: "Weekly Updates", icon: Sparkles, testId: "nav-weekly-updates" },
  { to: "/admin/settings", label: "Working Hours", icon: Clock, testId: "nav-admin-settings" },
];

const SIDEBAR_COLLAPSE_KEY = "sidebar-collapsed";

// Individual nav link — renders icon-only (with title tooltip) when collapsed,
// icon + label when expanded. Kept out of the component body so the collapse
// toggle doesn't re-declare it on every render.
function SidebarNavLink({ item, collapsed }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-sm text-sm transition-colors ${
          collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
        } ${
          isActive
            ? collapsed
              ? "bg-zinc-900 text-zinc-100"
              : "bg-zinc-900 text-zinc-100 border-l-2 border-amber-500 pl-[10px]"
            : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="font-medium">{item.label}</span>}
    </NavLink>
  );
}

export default function Layout() {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { brand, clearOverride } = useBrand();
  const [changePwOpen, setChangePwOpen] = useState(false);
  // Mobile drawer state — controls the slide-in menu on iOS / small screens.
  // Desktop uses the always-visible sidebar and ignores this flag.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Read collapse preference synchronously so the initial paint doesn't jump
  // between expanded → collapsed on hydration.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* localStorage might be blocked (private mode) — no-op is fine. */
    }
  }, [collapsed]);
  // Auto-close the mobile drawer whenever navigation completes, otherwise
  // tapping a link would leave the overlay covering the destination page.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);
  // Lock body scroll while the drawer is open — otherwise iOS Safari lets
  // you scroll the page behind the overlay which feels broken.
  useEffect(() => {
    if (mobileNavOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileNavOpen]);

  const isAdmin = user?.role === "admin";
  // Client-side users (linked to a Client via user.client_id) only see
  // the surfaces that make sense for them. Internal-only sections and
  // admin sections both get hidden.
  const isClientUser = !!user?.client_id;
  const visibleItems = navItems.filter((i) => {
    if (i.internalOnly && isClientUser) return false;
    if (i.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <div className="flex min-h-screen relative z-10 flex-col md:flex-row">
      {/* ─── Mobile top bar (iOS / small screens) ─────────────────
          The desktop sidebar is `hidden md:flex`, so without this bar
          phone users had literally no navigation. Sticky so it stays put
          when the page scrolls. */}
      <div
        data-testid="mobile-topbar"
        className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-2 px-4 h-14 border-b border-zinc-800/70 bg-zinc-950/95 backdrop-blur"
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          data-testid="mobile-nav-open-btn"
          aria-label="Open menu"
          className="p-2 -ml-2 rounded-sm text-zinc-300 hover:text-amber-400 hover:bg-zinc-900 transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <img
          src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
          alt="Whiplash Film & Creative"
          className="h-8 w-auto object-contain select-none"
          draggable="false"
          data-testid="mobile-brand-fallback-hidden"
          style={{ display: "none" }}
        />
        <div className="flex-1 flex justify-center">
          <BrandLogo size="sm" />
        </div>
        <button
          type="button"
          onClick={() => setChangePwOpen(true)}
          data-testid="mobile-user-menu-btn"
          aria-label="Account"
          className="w-8 h-8 rounded-full bg-zinc-900 text-[10px] font-mono font-bold text-amber-400 uppercase flex items-center justify-center"
        >
          {user
            ? (user.name || user.email || "?").trim().charAt(0).toUpperCase()
            : "?"}
        </button>
      </div>

      {/* ─── Mobile drawer + backdrop ────────────────────────────── */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          data-testid="mobile-nav-drawer-root"
        >
          <button
            type="button"
            aria-label="Close menu"
            data-testid="mobile-nav-backdrop"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <aside
            data-testid="mobile-nav-drawer"
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-950 border-r border-zinc-800/70 flex flex-col animate-in slide-in-from-left duration-200"
          >
            <div className="border-b border-zinc-800/70 flex items-center justify-between gap-2 px-4 py-4">
              <BrandLogo size="md" className="flex-1 min-w-0" />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                data-testid="mobile-nav-close-btn"
                aria-label="Close menu"
                className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-400 hover:bg-zinc-900 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="py-4 flex flex-col gap-1 flex-1 overflow-y-auto px-3">
              <div className="text-overline text-zinc-600 px-3 mb-3">Workspace</div>
              {visibleItems.map((item) =>
                item.to === "/clients" ? (
                  <ClientsNavGroup
                    key={item.to}
                    collapsed={false}
                    currentPath={location.pathname}
                    testIdPrefix="mobile-nav-clients"
                  />
                ) : (
                  <SidebarNavLink key={item.to} item={item} collapsed={false} />
                )
              )}
              {isAdmin && (
                <>
                  <div className="text-overline text-zinc-600 px-3 mb-3 mt-5">
                    Admin
                  </div>
                  {adminNavItems.map((item) => (
                    <SidebarNavLink key={item.to} item={item} collapsed={false} />
                  ))}
                </>
              )}
            </nav>
            <div className="border-t border-zinc-800/70 px-3 py-4">
              <div className="flex items-center gap-2 px-2 py-2">
                <button
                  type="button"
                  onClick={() => {
                    setChangePwOpen(true);
                    setMobileNavOpen(false);
                  }}
                  className="flex-1 min-w-0 text-left hover:bg-zinc-900/60 rounded-sm px-1 py-0.5 -mx-1 transition-colors"
                >
                  {user && (
                    <>
                      <div className="text-xs text-zinc-300 truncate">
                        {user.name || user.email}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        {user.role}
                      </div>
                    </>
                  )}
                </button>
                <button
                  onClick={logout}
                  data-testid="mobile-sign-out-btn"
                  title="Sign out"
                  className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-900 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      <aside
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`hidden md:flex ${
          collapsed ? "w-14" : "w-64"
        } flex-col border-r border-zinc-800/70 bg-zinc-950/80 backdrop-blur-md sticky top-0 h-screen transition-[width] duration-200 ease-out`}
      >
        {/* Brand + collapse toggle */}
        <div
          className={`border-b border-zinc-800/70 flex items-center ${
            collapsed ? "px-2 py-4 justify-center" : "px-4 py-5 justify-between gap-2"
          }`}
        >
          {!collapsed && (
            <div data-testid="brand-logo" className="flex-1 min-w-0">
              <BrandLogo size="lg" />
            </div>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar (more room for content)"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-400 hover:bg-zinc-900 transition-colors shrink-0"
          >
            {collapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
        </div>

        <nav
          className={`py-4 flex flex-col gap-1 flex-1 ${
            collapsed ? "px-2" : "px-3"
          }`}
        >
          {!collapsed && (
            <div className="text-overline text-zinc-600 px-3 mb-3">Workspace</div>
          )}
          {visibleItems.map((item) =>
            item.to === "/clients" ? (
              <ClientsNavGroup
                key={item.to}
                collapsed={collapsed}
                currentPath={location.pathname}
                testIdPrefix="nav-clients"
              />
            ) : (
              <SidebarNavLink key={item.to} item={item} collapsed={collapsed} />
            )
          )}
          {isAdmin && (
            <>
              {!collapsed ? (
                <div className="text-overline text-zinc-600 px-3 mb-3 mt-5">
                  Admin
                </div>
              ) : (
                <div
                  className="my-3 mx-2 border-t border-zinc-800/60"
                  aria-hidden="true"
                />
              )}
              {adminNavItems.map((item) => (
                <SidebarNavLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>

        <div
          className={`border-t border-zinc-800/70 ${
            collapsed ? "px-2 py-3" : "px-3 py-4"
          }`}
        >
          <div
            className={`flex items-center ${
              collapsed ? "flex-col gap-2" : "gap-2 px-2 py-2"
            }`}
          >
            {!collapsed ? (
              <button
                type="button"
                onClick={() => setChangePwOpen(true)}
                data-testid="user-menu-btn"
                title="Change my password"
                className="flex-1 min-w-0 text-left hover:bg-zinc-900/60 rounded-sm px-1 py-0.5 -mx-1 transition-colors"
              >
                {user && (
                  <>
                    <div className="text-xs text-zinc-300 truncate">
                      {user.name || user.email}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {user.role}
                    </div>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setChangePwOpen(true)}
                data-testid="user-menu-btn"
                title={user ? `${user.name || user.email} · Change password` : "Account"}
                className="w-8 h-8 rounded-full bg-zinc-900 hover:bg-zinc-800 text-[10px] font-mono font-bold text-amber-400 uppercase transition-colors flex items-center justify-center"
              >
                {user
                  ? (user.name || user.email || "?").trim().charAt(0).toUpperCase()
                  : "?"}
              </button>
            )}
            <button
              onClick={logout}
              data-testid="sign-out-btn"
              title="Sign out"
              className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-900 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          {/* Admin-only white-label QA chip — hidden for non-admins so
              client demo accounts see a seamless experience. */}
          {isAdmin && brand.id !== "default" && !collapsed && (
            <button
              type="button"
              data-testid="white-label-active-chip"
              onClick={clearOverride}
              title={`White-label override active — click to exit ${brand.name} theme`}
              className="mt-2 w-full flex items-center justify-between gap-2 rounded-sm border px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors"
              style={{
                borderColor: "var(--brand-accent)",
                background: "var(--brand-accent-soft)",
                color: "var(--brand-accent)",
              }}
            >
              <span>White-Label · {brand.name}</span>
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet key={location.pathname} />
      </main>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
